import Box from '@spotnana/pixel-react/dist/Box';
import Divider from '@spotnana/pixel-react/dist/Divider';
import Grid from '@spotnana/pixel-react/dist/Grid';
import {
  IPaymentInfoApplicableToEnum,
  IUserOrgId,
  travelerPaymentMethodsTranslationKeys,
  settingsTranslationKeys,
  useGetRequestHeader,
  useCardAccess,
  useMountEffect,
  usePartnerFeatures,
  ConfirmCardSaveEnum,
} from 'obt-common';
import { CreditCardAccessType } from '@spotnana/types/openapi/models/credit-card-access-type';
import { useEffect, useCallback, useState } from 'react';
import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { Checkbox } from '@spotnana/blocks/src/Checkbox';
import { Typography } from '@spotnana/blocks/src/Typography';
import { RazorPayRedirectModal } from 'src/app/payments/shared/RazorPay/RazorPayRedirectModal';
import { flex, margins } from '@spotnana/blocks/src/utils/styling';
import { onConfirmCardSave } from 'src/app/payments/shared/utils/onConfirmCardSave';
import { ShowIndianPaymentGuidelinesMsg } from 'src/app/payments/shared/ShowIndianPaymentGuidelinesMsg';
import { useConfirmCardSave } from 'src/app/payments/shared/hooks/useConfirmCardSave';
import AddressDetailsForm from './AddressDetailsForm';
import ApplicableToOptions from './ApplicableToOptions';
import CardDetailsForm from './CardDetailsForm';
import ErrorBoundary from '../../../ErrorBoundary';
import ProfileModal from '../ProfileModal';
import usePaymentForm from '../../../../hooks/usePaymentForm';
import LegalEntityForm from './LegalEntityForm';
import CurrencySectionContainer from './CurrencySection';

const { BILLING_ADDRESS_TITLE, CARD_TITLE, CARD_INFO_TITLE } = travelerPaymentMethodsTranslationKeys;
const { CENTRAL_CARD_APPLICABLE_TEXT } = settingsTranslationKeys;

export interface IPaymentModalProps {
  onClose: () => void;
  userOrgId: IUserOrgId;
  onUpdate: (successMessage: string) => Promise<void>;
  authToken: string;
  organizationId?: string;
  cardAccessType?: CreditCardAccessType;
  includePersonalCards?: boolean;
  includeCentralCards?: boolean;
  showCurrencySection?: boolean;
  showApplicableTo?: boolean;
  noBackDrop?: boolean;
  showOwnershipLabelSelector?: boolean;
}

const CreateCardModal: React.FC<IPaymentModalProps> = ({
  onClose,
  userOrgId,
  onUpdate,
  authToken,
  organizationId,
  cardAccessType = undefined,
  includePersonalCards = true,
  includeCentralCards = false,
  showCurrencySection = false,
  showApplicableTo = true,
  noBackDrop = false,
  showOwnershipLabelSelector = true,
}: IPaymentModalProps) => {
  const { t: tAdmin } = useTranslation('ADMIN');
  const { t } = useTranslation('PROFILE');
  const { t: tt } = useTranslation('WEB');
  const { travelerInfo } = useGetRequestHeader();
  const getCardAccess = useCardAccess({ cardAccessType });
  const { confirmCard } = useConfirmCardSave();

  const { features } = usePartnerFeatures();
  const { centralCardServiceFeeFeatureEnabled } = features;
  const isCentralCard = cardAccessType === CreditCardAccessType.Centralised;

  const [selectedLegalEntityIds, setSelectedLegalEntityIds] = useState<string[]>([]);
  const [openRazorPayRedirectModal, setOpenRazorPayRedirectModal] = useState<boolean>(false);
  const [paymentAuthUrl, setPaymentAuthUrl] = useState<string>('');

  const {
    isVgsElementsInitialized,
    handleSave,
    errorState,
    saving,
    applicableTo: { value: applicableTo, setValue: updateApplicableTo },
    address: { value: address, setValue: setAddress, errorState: addressErrorState },
    cardAccess: { setValue: setCardAccess },
    currency: { value: currency, setValue: setCurrency, errorState: currencyErrorState },
    cardLabel: { value: cardLabel, setValue: setCardLabel, errorState: labelErrorState },
    ownershipLabel: { value: ownershipLabel, setValue: setOwnershipLabel },
  } = usePaymentForm({
    userOrgId,
    authToken,
    travelerInfoHeader: travelerInfo,
    initialApplicableTo:
      cardAccessType === CreditCardAccessType.Centralised ? [IPaymentInfoApplicableToEnum.AIR] : undefined,
    includeCentralCards,
    includePersonalCards,
    shouldValidateCurrency: showCurrencySection,
    prefix: '',
    shouldValidateOwnershipLabel: showOwnershipLabelSelector,
    onConfirmCardSave,
  });

  const onCardSaveFailed = useCallback(
    () => (): void => {
      onClose();
    },
    [onClose],
  );

  useEffect(() => {
    window.onmessage = async (event) => {
      if (event.data.type === ConfirmCardSaveEnum.INITIALIZE) {
        try {
          const { response, trackCardCreationData } = event.data.data;

          await confirmCard({
            response,
            trackCardCreationData,
            setPaymentAuthUrl,
            setOpenRazorPayRedirectModal,
            travelerInfo: {
              authToken,
              userOrgId: userOrgId ?? {},
            },
            onUpdate,
            onClose,
          });
        } catch (e) {
          console.error(e);
          onCardSaveFailed();
        }
      }
    };
  }, [authToken, confirmCard, onCardSaveFailed, onClose, onUpdate, userOrgId]);

  const setLegalEntityIds = useCallback(
    ({ selectedItemIds, areAllItemsSelected }: { selectedItemIds: string[]; areAllItemsSelected: boolean }) => {
      const result = getCardAccess({
        areAllItemsSelected,
        selectedItemIds,
        organizationId,
      });

      setSelectedLegalEntityIds(selectedItemIds);
      setCardAccess(result?.cardAccess);
    },
    [setCardAccess, getCardAccess, organizationId],
  );

  useMountEffect(() => {
    setCardAccess(
      getCardAccess({
        organizationId,
      })?.cardAccess,
    );
  });

  return (
    <ProfileModal
      isNewEntry
      onClose={onClose}
      onSave={handleSave}
      title={t(CARD_TITLE)}
      dataTestId="add-payment"
      isLoadingOrError={!isVgsElementsInitialized}
      loadingMessage={tt('Opening secure form ...')}
      isSaving={saving}
      noBackDrop={noBackDrop}
    >
      <form>
        <ErrorBoundary>
          <Grid.Row minWidth={{ xs: '300px', md: '720px' }}>
            <Grid.Col xs={12} sm={6}>
              <Typography variant="body3" kind="semibold">
                {t(CARD_INFO_TITLE)}
              </Typography>

              <Box my={10}>
                <Divider />
              </Box>

              <CardDetailsForm
                labelErrorState={labelErrorState}
                cardLabel={cardLabel}
                setCardLabel={setCardLabel}
                ownershipLabel={ownershipLabel}
                setOwnershipLabel={setOwnershipLabel}
                errorState={errorState}
                prefix=""
                showOwnershipLabelSelector={showOwnershipLabelSelector}
              />
              {isCentralCard && (
                <Typography variant="body2" color="secondary">
                  {tAdmin(CENTRAL_CARD_APPLICABLE_TEXT)}
                </Typography>
              )}
              {showApplicableTo && (
                <Box data-testid="non-central-cards-content">
                  <ApplicableToOptions
                    applicableTo={applicableTo}
                    updateApplicableTo={updateApplicableTo}
                    applicableToError={`${errorState.error.applicableTo}`}
                  />
                </Box>
              )}
              {isCentralCard && centralCardServiceFeeFeatureEnabled && (
                <>
                  <Divider my="16px" />
                  <Checkbox
                    checked={applicableTo.includes(IPaymentInfoApplicableToEnum.SERVICE_FEE)}
                    onChange={updateApplicableTo(IPaymentInfoApplicableToEnum.SERVICE_FEE)}
                    label={tt('Use this card to charge $t(service fees).')}
                    subtext={tt('(All $t(service fees) will be charged to this card (air, hotel, car, rail))')}
                  />
                  <Divider mt="16px" mb="30px" />
                </>
              )}

              <LegalEntityForm
                isCentralCard={isCentralCard}
                itemIds={selectedLegalEntityIds}
                setItemIds={setLegalEntityIds}
                organizationId={organizationId}
              />
              <CurrencySectionContainer
                currency={currency}
                showCurrencySection={showCurrencySection}
                error={currencyErrorState.error}
                onCurrencyChange={setCurrency}
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <Typography variant="body3" kind="semibold">
                {t(BILLING_ADDRESS_TITLE)}
              </Typography>

              <Box my={10}>
                <Divider />
              </Box>

              {isVgsElementsInitialized && (
                <AddressDetailsForm address={address} setAddress={setAddress} errorState={addressErrorState} />
              )}
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <div css={[flex.init, flex.wrap.wrap, margins.xl.m1, margins.y.m1point25]}>
              <ShowIndianPaymentGuidelinesMsg userOrgId={userOrgId} regionCode={address.regionCode} />
            </div>
          </Grid.Row>
        </ErrorBoundary>
      </form>
      <RazorPayRedirectModal
        open={openRazorPayRedirectModal}
        closeModal={() => setOpenRazorPayRedirectModal(false)}
        paymentAuthUrl={paymentAuthUrl}
      />
    </ProfileModal>
  );
};

export default CreateCardModal;
